import { useMemo, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useResource, formatDate, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { AvailabilityPattern, Employee, LeaveRequest } from "../lib/types";

const LEAVE_TYPE = ["annual", "personal", "carers", "unpaid", "parental", "long_service", "other"]
    .map((t) => ({ value: t, label: titleCase(t) }));

const LEAVE_STATUS = ["requested", "approved", "declined", "cancelled"]
    .map((s) => ({ value: s, label: titleCase(s) }));

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_OPTIONS = DAYS.map((d, i) => ({ value: String(i + 1), label: d }));

const statusChip = (s: LeaveRequest["status"]) =>
    s === "approved" ? "pass" : s === "requested" ? "warn" : s === "declined" ? "block" : "mute";

interface LeaveFull extends LeaveRequest {
    decided_at: string | null;
    decision_note: string | null;
}

type Tab = "leave" | "availability" | "taken";

/** Whole days inclusive of both ends — a single-day request counts as 1. */
const daysBetween = (start: string, end: string): number =>
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000) + 1;

type Dialog =
    | { kind: "createLeave" }
    | { kind: "editLeave"; leave: LeaveFull }
    | { kind: "approve"; leave: LeaveFull }
    | { kind: "decline"; leave: LeaveFull }
    | { kind: "createAvailability" }
    | { kind: "editAvailability"; pattern: AvailabilityPattern }
    | { kind: "deleteAvailability"; pattern: AvailabilityPattern; name: string }
    | null;

export const LeaveAndAvailability = () => {
    const { user } = useAuth();
    const canDecide = user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";
    const canManageAvailability = user?.role === "admin" || user?.role === "manager";

    const leave = useResource<LeaveFull[]>("/leave-requests");
    const availability = useResource<AvailabilityPattern[]>("/availability");
    const employees = useResource<Employee[]>("/employees");

    const [tab, setTab] = useState<Tab>("leave");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { leave.reload(); availability.reload(); setDialog(null); };

    const employeeName = useMemo(() => {
        const map = new Map((employees.data ?? []).map((e) => [e.id, e.full_name]));
        return (id: string) => map.get(id) ?? "Unknown";
    }, [employees.data]);

    const employeeOptions = (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name }));

    // Staff raise leave only for themselves — the database enforces it, so
    // offering a picker they cannot use would be a lie.
    const isStaff = user?.role === "staff";
    const leaveFields: Field[] = [
        ...(isStaff ? [] : [{
            name: "employee_id", label: "Employee", kind: "select" as const,
            required: true, options: employeeOptions,
        }]),
        { name: "leave_type", label: "Leave type", kind: "select", required: true, options: LEAVE_TYPE, half: true },
        ...(canDecide ? [{
            name: "status", label: "Status", kind: "select" as const, options: LEAVE_STATUS, half: true,
        }] : []),
        { name: "start_date", label: "From", kind: "date", required: true, half: true },
        { name: "end_date", label: "To", kind: "date", required: true, half: true },
        { name: "reason", label: "Reason", kind: "textarea" },
    ];

    const availabilityFields: Field[] = [
        { name: "employee_id", label: "Employee", kind: "select", required: true, options: employeeOptions },
        { name: "day_of_week", label: "Day", kind: "select", required: true, options: DAY_OPTIONS, half: true },
        { name: "start_time", label: "Available from", kind: "time", required: true, half: true },
        { name: "end_time", label: "Available until", kind: "time", required: true, half: true },
        { name: "effective_from", label: "Effective from", kind: "date", half: true },
        { name: "effective_to", label: "Effective to", kind: "date", half: true,
          help: "Leave blank for an ongoing pattern." },
    ];

    const allLeave = leave.data ?? [];
    const filteredLeave = allLeave.filter((l) =>
        matches(search, employeeName(l.employee_id), l.reason) &&
        (!filters.status || l.status === filters.status) &&
        (!filters.leave_type || l.leave_type === filters.leave_type)
    );

    const allAvailability = availability.data ?? [];
    const filteredAvailability = allAvailability.filter((a) =>
        matches(search, employeeName(a.employee_id)) &&
        (!filters.employee_id || a.employee_id === filters.employee_id)
    );

    // Days actually taken this calendar year, by employee and leave type — not
    // a "balance": nothing in the schema records an entitlement or accrual to
    // subtract against, so this reports what happened, not what remains.
    const currentYear = new Date().getFullYear();
    const takenByEmployee = useMemo(() => {
        const totals = new Map<string, Record<string, number>>();
        for (const request of allLeave) {
            if (request.status !== "approved") continue;
            if (new Date(request.start_date).getFullYear() !== currentYear) continue;
            const row = totals.get(request.employee_id) ?? {};
            row[request.leave_type] = (row[request.leave_type] ?? 0) + daysBetween(request.start_date, request.end_date);
            totals.set(request.employee_id, row);
        }
        return totals;
    }, [allLeave, currentYear]);

    const takenTypes = ["annual", "personal", "carers", "unpaid", "parental", "long_service", "other"] as const;

    if (leave.error) return <div className="error-banner">{leave.error}</div>;

    return (
        <div>
            {dialog?.kind === "createLeave" && (
                <EntityForm title="Request Leave" path="/leave-requests" fields={leaveFields}
                    fixed={isStaff ? { employee_id: user?.employeeId, status: "requested" } : undefined}
                    subtitle="Approved leave blocks rostering for those dates. A manager can override that block with a written justification, but it is recorded."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "editLeave" && (
                <EntityForm title="Edit leave request" path="/leave-requests"
                    fields={leaveFields} initial={dialog.leave}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "approve" && (
                <ConfirmDialog
                    title={`Approve leave for ${employeeName(dialog.leave.employee_id)}?`}
                    message={`${formatDate(dialog.leave.start_date)} to ${formatDate(dialog.leave.end_date)}.`}
                    warning="Once approved, the roster will refuse to schedule them across these dates. Existing shifts in that window are not cancelled automatically — check the roster."
                    confirmLabel="Approve leave"
                    request={{ method: "post", path: `/leave-requests/${dialog.leave.id}/approve` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "decline" && (
                <ConfirmDialog
                    title={`Decline leave for ${employeeName(dialog.leave.employee_id)}?`}
                    tone="danger"
                    message={`${formatDate(dialog.leave.start_date)} to ${formatDate(dialog.leave.end_date)}.`}
                    warning="Your reason is stored on the request and in the audit log, and is visible to the employee."
                    confirmLabel="Decline leave" requireReason reasonLabel="Reason for declining"
                    request={{ method: "post", path: `/leave-requests/${dialog.leave.id}/decline` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "createAvailability" && (
                <EntityForm title="Add availability" path="/availability" fields={availabilityFields}
                    subtitle="Rostering outside a stated pattern is allowed, but it is flagged as a warning on the shift."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "editAvailability" && (
                <EntityForm title="Edit availability" path="/availability"
                    fields={availabilityFields} initial={dialog.pattern}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "deleteAvailability" && (
                <ConfirmDialog
                    title={`Remove this availability for ${dialog.name}?`}
                    tone="danger"
                    message={`${DAYS[dialog.pattern.day_of_week - 1]}, ${dialog.pattern.start_time}–${dialog.pattern.end_time}.`}
                    warning="With no pattern recorded for a day, every shift on that day is flagged as outside stated availability."
                    confirmLabel="Remove pattern"
                    request={{ method: "delete", path: `/availability/${dialog.pattern.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <div className="tabs">
                <button className={tab === "leave" ? "active" : ""} onClick={() => { setTab("leave"); setFilters({}); }}>
                    Leave requests ({allLeave.filter((l) => l.status === "requested").length} pending)
                </button>
                <button className={tab === "availability" ? "active" : ""} onClick={() => { setTab("availability"); setFilters({}); }}>
                    Availability patterns
                </button>
                <button className={tab === "taken" ? "active" : ""} onClick={() => { setTab("taken"); setFilters({}); }}>
                    Leave taken ({currentYear})
                </button>
            </div>

            {tab === "leave" ? (
                <>
                    <FilterBar
                        search={search} onSearch={setSearch}
                        searchPlaceholder="Search leave requests…"
                        values={filters}
                        onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                        filters={[
                            { name: "status", allLabel: "All Status", options: LEAVE_STATUS },
                            { name: "leave_type", allLabel: "All Types", options: LEAVE_TYPE },
                        ]}
                        resultNote={filteredLeave.length !== allLeave.length
                            ? `Showing ${filteredLeave.length} of ${allLeave.length} requests` : undefined}
                        action={<button className="btn" onClick={() => setDialog({ kind: "createLeave" })}>+ Request Leave</button>}
                    />

                    <div className="card">
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Employee</th><th>Type</th><th>From</th><th>To</th>
                                            <th>Reason</th><th>Status</th>
                                            {canDecide && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLeave.map((request) => (
                                            <tr key={request.id}>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                        <InitialsAvatar name={employeeName(request.employee_id)} size={26} />
                                                        {employeeName(request.employee_id)}
                                                    </div>
                                                </td>
                                                <td>{titleCase(request.leave_type)}</td>
                                                <td className="mono">{formatDate(request.start_date)}</td>
                                                <td className="mono">{formatDate(request.end_date)}</td>
                                                <td style={{ color: "var(--ink-soft)" }}>
                                                    {request.reason ?? "—"}
                                                    {request.decision_note && (
                                                        <div style={{ fontSize: "0.73rem", color: "var(--muted)", marginTop: 2 }}>
                                                            Decision: {request.decision_note}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`chip ${statusChip(request.status)}`}>
                                                        {titleCase(request.status)}
                                                    </span>
                                                    {request.decided_at && (
                                                        <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: 2 }}>
                                                            {formatDate(request.decided_at)}
                                                        </div>
                                                    )}
                                                </td>
                                                {canDecide && (
                                                    <td>
                                                        <div className="row-actions">
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "editLeave", leave: request })}>Edit</button>
                                                            {request.status === "requested" && (
                                                                <>
                                                                    <button className="icon-btn" onClick={() => setDialog({ kind: "approve", leave: request })}>Approve</button>
                                                                    <button className="icon-btn danger" onClick={() => setDialog({ kind: "decline", leave: request })}>Decline</button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {filteredLeave.length === 0 && (
                                            <tr><td colSpan={canDecide ? 7 : 6} className="empty">No leave requests match.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            ) : tab === "availability" ? (
                <>
                    <FilterBar
                        search={search} onSearch={setSearch}
                        searchPlaceholder="Search by employee…"
                        values={filters}
                        onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                        filters={[{ name: "employee_id", allLabel: "All Employees", options: employeeOptions }]}
                        resultNote={filteredAvailability.length !== allAvailability.length
                            ? `Showing ${filteredAvailability.length} of ${allAvailability.length} patterns` : undefined}
                        action={canManageAvailability && (
                            <button className="btn" onClick={() => setDialog({ kind: "createAvailability" })}>
                                + Add Availability
                            </button>
                        )}
                    />

                    <div className="card">
                        <div className="card-body flush">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Employee</th><th>Day</th><th>From</th><th>Until</th><th>Effective</th>
                                            {canManageAvailability && <th style={{ textAlign: "right" }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAvailability.map((pattern) => (
                                            <tr key={pattern.id}>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                        <InitialsAvatar name={employeeName(pattern.employee_id)} size={26} />
                                                        {employeeName(pattern.employee_id)}
                                                    </div>
                                                </td>
                                                <td>{DAYS[pattern.day_of_week - 1]}</td>
                                                <td className="mono">{pattern.start_time}</td>
                                                <td className="mono">{pattern.end_time}</td>
                                                <td className="mono">
                                                    {formatDate(pattern.effective_from)}
                                                    {pattern.effective_to ? ` – ${formatDate(pattern.effective_to)}` : " – ongoing"}
                                                </td>
                                                {canManageAvailability && (
                                                    <td>
                                                        <div className="row-actions">
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "editAvailability", pattern })}>Edit</button>
                                                            <button className="icon-btn danger" onClick={() => setDialog({
                                                                kind: "deleteAvailability", pattern,
                                                                name: employeeName(pattern.employee_id),
                                                            })}>Remove</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {filteredAvailability.length === 0 && (
                                            <tr><td colSpan={canManageAvailability ? 6 : 5} className="empty">No availability patterns match.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="card">
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        {takenTypes.map((t) => <th key={t}>{titleCase(t)}</th>)}
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(employees.data ?? [])
                                        .filter((e) => takenByEmployee.has(e.id))
                                        .map((e) => {
                                            const row = takenByEmployee.get(e.id) ?? {};
                                            const total = Object.values(row).reduce((sum, n) => sum + n, 0);
                                            return (
                                                <tr key={e.id}>
                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                                            <InitialsAvatar name={e.full_name} size={26} />
                                                            {e.full_name}
                                                        </div>
                                                    </td>
                                                    {takenTypes.map((t) => (
                                                        <td key={t} className="mono">{row[t] ? `${row[t]}d` : "—"}</td>
                                                    ))}
                                                    <td className="mono" style={{ fontWeight: 600 }}>{total}d</td>
                                                </tr>
                                            );
                                        })}
                                    {takenByEmployee.size === 0 && (
                                        <tr><td colSpan={takenTypes.length + 2} className="empty">No approved leave taken in {currentYear} yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
